/**
 * TaskCategories - Página de gerenciamento de categorias de tarefas
 * 
 * Características:
 * - Listagem em cards com KPIs (total, ativas, fases, tarefas)
 * - CRUD completo via Dialog
 * - Seleção de cor
 * - Toggle de status ativo/inativo
 * 
 * @author Sistema
 * @version 1.3.0
 * RELEASE: 2026-10-02 v1.3.0 - Fases gerenciadas no próprio card (trilha + "Gerenciar fases"),
 *          gerenciador abre automaticamente após criar categoria
 * RELEASE: 2026-08-22 v1.2.0 - Título/subtítulo dinâmicos por módulo (Tarefas | Retenção | Jurídico)
 * RELEASE: 2026-04-06 v1.0.0 - Criação da página
 * RELEASE: 2026-04-19 v1.1.0 - Alinhamento ao padrão de header e KPIs do módulo de Contatos
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  FolderTree,
  Loader2,
  Search,
  MoreHorizontal,
  Layers,
  ClipboardList,
  CheckCircle,
  Columns,
  ChevronRight,
} from 'lucide-react';
import { useTaskPhases, type TaskPhase } from '@/hooks/useTaskPhases';
import { CategoryPhaseManager } from '@/components/tasks/categories/CategoryPhaseManager';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTaskCategories, TaskCategory, UpsertTaskCategoryInput } from '@/hooks/useTaskCategories';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { HexColorPicker } from '@/components/tasks/HexColorPicker';

// ============================================================================
// CORES DISPONÍVEIS PARA SELEÇÃO
// ============================================================================

const AVAILABLE_COLORS = [
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#f43f5e', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4',
  '#0ea5e9', '#64748b', '#78716c', '#475569',
];

// ============================================================================
// COMPONENTE DE KPI
// ============================================================================

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  variant?: 'primary' | 'success' | 'info' | 'purple';
}

/** Mapeamento de variantes para cores (padrão unificado DealListKpiCards/Contatos) */
const categoryVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de KPI compacto horizontal (padrão unificado) */
function MetricCard({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) {
  const styles = categoryVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function TaskCategories() {
  const { t } = useTranslation('common');
  // v1.2.0: módulo corrente (Tarefas | Retenção | Jurídico) para rótulos dinâmicos
  const taskModule = useTaskModule();
  const {
    categories,
    isLoading,
    upsert,
    isUpserting,
    remove,
    isRemoving,
  } = useTaskCategories();

  // Estado do Dialog de criar/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TaskCategory | null>(null);
  const [search, setSearch] = useState('');

  // Estado do formulário
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(AVAILABLE_COLORS[0]);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);
  // Marcador "Padrão" da categoria (opcional)
  const [formIsDefault, setFormIsDefault] = useState(false);

  // Dialog de confirmação para exclusão
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // v1.3.0: fases de todas as categorias do módulo (uma única consulta) para a trilha nos cards
  const { phases: allPhases } = useTaskPhases();
  const phasesByCategory = useMemo(() => {
    const map: Record<string, TaskPhase[]> = {};
    for (const p of allPhases) (map[p.category_id] ||= []).push(p);
    Object.values(map).forEach((list) => list.sort((a, b) => a.sort_order - b.sort_order));
    return map;
  }, [allPhases]);

  // v1.3.0: categoria com o gerenciador de fases aberto
  const [phaseManagerCategory, setPhaseManagerCategory] = useState<
    { id: string; name: string; color?: string | null } | null
  >(null);

  // KPIs calculadas
  const totalCount = categories.length;
  const activeCount = categories.filter(c => c.is_active).length;
  const totalPhases = categories.reduce((sum, c) => sum + c.phases_count, 0);
  const totalTasks = categories.reduce((sum, c) => sum + c.tasks_count, 0);

  // Categorias filtradas por busca
  const filtered = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.description?.toLowerCase().includes(search.toLowerCase())
  );

  /**
   * Abre o dialog para criar nova categoria
   */
  const handleCreate = () => {
    setEditingCategory(null);
    setFormName('');
    setFormDescription('');
    setFormColor(AVAILABLE_COLORS[0]);
    setFormSortOrder(categories.length + 1);
    setFormIsActive(true);
    setFormIsDefault(false);
    setDialogOpen(true);
  };

  /**
   * Abre o dialog para editar categoria existente
   */
  const handleEdit = (category: TaskCategory) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormDescription(category.description || '');
    setFormColor(category.color || AVAILABLE_COLORS[0]);
    setFormSortOrder(category.sort_order);
    setFormIsActive(category.is_active);
    setFormIsDefault(!!category.is_default);
    setDialogOpen(true);
  };

  /**
   * Salva a categoria (criar ou atualizar)
   */
  const handleSave = async () => {
    if (!formName.trim()) return;

    const input: UpsertTaskCategoryInput = {
      p_name: formName.trim(),
      p_description: formDescription.trim() || undefined,
      p_color: formColor,
      p_sort_order: formSortOrder,
      p_is_active: formIsActive,
      p_is_default: formIsDefault,
    };

    if (editingCategory) {
      input.p_id = editingCategory.id;
    }

    const result = await upsert(input);
    setDialogOpen(false);

    // v1.3.0: após criar, abre o gerenciador de fases da nova categoria
    if (!editingCategory) {
      const newId =
        typeof result === 'string'
          ? result
          : (result as { id?: string } | null)?.id;
      if (newId) {
        setPhaseManagerCategory({ id: newId, name: input.p_name, color: input.p_color });
      }
    }
  };

  /**
   * Exclui uma categoria com confirmação
   */
  const handleDelete = async (category: TaskCategory) => {
    const confirmed = await confirmDialog({
      title: 'Excluir Categoria',
      description: `Deseja realmente excluir a categoria "${category.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (confirmed) {
      await remove(category.id);
    }
  };

  // Estado de carregamento
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 pb-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto (padrão Contatos) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Categorias de {taskModule.label}</h1>
          <p className="text-xs text-muted-foreground">
            {`Gerencie as categorias para organizar ${taskModule.label.toLowerCase()}`}
          </p>
        </div>
        <Button
          size="xs"
          onClick={handleCreate}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5" />
          Nova Categoria
        </Button>
      </div>

      {/* KPIs (padrão horizontal compacto) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
        <MetricCard title="Total" value={totalCount} icon={FolderTree} variant="primary" />
        <MetricCard title="Ativas" value={activeCount} icon={CheckCircle} variant="success" />
        <MetricCard title="Fases" value={totalPhases} icon={Layers} variant="info" />
        <MetricCard title="Tarefas" value={totalTasks} icon={ClipboardList} variant="purple" />
      </div>

      {/* Busca */}
      <div className="relative max-w-sm px-4 pb-3">
        <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar categorias..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="px-4 pb-4 space-y-4">
      {/* Lista de Categorias */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <FolderTree className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">Nenhuma categoria encontrada</p>
            <p className="text-sm mt-1">
              {search ? 'Tente ajustar sua busca' : 'Crie a primeira categoria para começar'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((category) => (
            <Card key={category.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-0">
                {/* Barra de cor no topo */}
                <div
                  className="h-1.5"
                  style={{ backgroundColor: category.color || '#64748b' }}
                />
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: category.color || '#64748b' }}
                      >
                        <FolderTree className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{category.name}</h3>
                        {category.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(category)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPhaseManagerCategory(category)}>
                          <Columns className="h-4 w-4 mr-2" />
                          Gerenciar fases
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(category)}
                          className="text-destructive focus:text-destructive"
                          disabled={category.tasks_count > 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* v1.3.0: trilha das fases + atalho para gerenciar (padrão card de Workspace) */}
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex items-center gap-1 flex-wrap min-w-0 flex-1">
                      {(phasesByCategory[category.id] || []).slice(0, 5).map((ph, idx, arr) => (
                        <span key={ph.id} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ph.color }} />
                          <span className="truncate max-w-[80px]">{ph.name}</span>
                          {idx < arr.length - 1 && <ChevronRight className="h-3 w-3 opacity-50" />}
                        </span>
                      ))}
                      {(phasesByCategory[category.id]?.length || 0) > 5 && (
                        <span className="text-[11px] text-muted-foreground">
                          +{phasesByCategory[category.id].length - 5}
                        </span>
                      )}
                      {!phasesByCategory[category.id]?.length && (
                        <span className="text-[11px] text-muted-foreground">Sem fases</span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => setPhaseManagerCategory(category)}
                    >
                      <Columns className="h-3.5 w-3.5 mr-1" />
                      Fases
                    </Button>
                  </div>

                  {/* Badges de contagem */}
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="secondary" className="text-xs">
                      {category.phases_count} fases
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {category.tasks_count} tarefas
                    </Badge>
                    {/* Marcador "Padrão" (opcional) */}
                    {category.is_default && (
                      <Badge variant="outline" className="text-[10px]">Padrão</Badge>
                    )}
                    {/* Status — padrão unificado (Badge outline + StatusDot) */}
                    <Badge
                      variant="outline"
                      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider ml-auto"
                    >
                      <StatusDot tone={category.is_active ? 'emerald' : 'muted'} />
                      {category.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>


      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
          {/* Cabeçalho padrão premium (mesmo do modal "Nova anotação") */}
          <PremiumDialogHeader
            icon={FolderTree}
            title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            description={
              editingCategory
                ? 'Atualize os dados da categoria de tarefas'
                : 'Preencha os dados para criar uma nova categoria'
            }
          />

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex: Desenvolvimento, Marketing..."
                maxLength={100}
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Descrição opcional da categoria"
                rows={3}
                maxLength={500}
              />
            </div>

            {/* Cor e Ordem */}
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <HexColorPicker value={formColor} onChange={setFormColor} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sort_order">Ordem</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 1)}
                  min={1}
                />
              </div>
            </div>

            {/* Status Ativo */}
            {editingCategory && (
              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Ativa</Label>
                <Switch
                  id="is_active"
                  checked={formIsActive}
                  onCheckedChange={setFormIsActive}
                />
              </div>
            )}

            {/* Categoria Padrão (opcional) — pré-seleciona e trava a categoria
                na criação de solicitações de Retenção/Jurídico */}
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="is_default">Categoria padrão</Label>
                <p className="text-xs text-muted-foreground">
                  Opcional. Novas solicitações já nascem nesta categoria.
                </p>
              </div>
              <Switch
                id="is_default"
                checked={formIsDefault}
                onCheckedChange={setFormIsDefault}
              />
            </div>
          </div>

          <DialogFooter className="border-t bg-muted/30 px-6 py-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formName.trim() || isUpserting}
            >
              {isUpserting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCategory ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      {ConfirmDialogComponent}

      {/* v1.3.0: gerenciador de fases da categoria */}
      <CategoryPhaseManager
        open={!!phaseManagerCategory}
        onOpenChange={(o) => !o && setPhaseManagerCategory(null)}
        category={phaseManagerCategory}
      />
    </div>
  );
}
